import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * What a text widget's content describes. Only `manual` is produced today; `widget` and `tab`
 * exist for the change that moves per-widget and per-tab AI summaries into text widgets.
 */
export type AppTextWidgetSourceType = "manual" | "widget" | "tab";

export type AppTextWidgetSourceWidgetType = "chart" | "table" | "kpi" | "nugget_feed";

const APP_TEXT_WIDGET_SOURCE_TYPES: readonly AppTextWidgetSourceType[] = [
  "manual",
  "widget",
  "tab",
];

const APP_TEXT_WIDGET_SOURCE_WIDGET_TYPES: readonly AppTextWidgetSourceWidgetType[] = [
  "chart",
  "table",
  "kpi",
  "nugget_feed",
];

export function isAppTextWidgetSourceType(value: unknown): value is AppTextWidgetSourceType {
  return (
    typeof value === "string" &&
    (APP_TEXT_WIDGET_SOURCE_TYPES as readonly string[]).includes(value)
  );
}

function isSourceWidgetType(value: unknown): value is AppTextWidgetSourceWidgetType | null {
  return (
    value === null ||
    (typeof value === "string" &&
      (APP_TEXT_WIDGET_SOURCE_WIDGET_TYPES as readonly string[]).includes(value))
  );
}

type AppTextWidgetSourceFields = {
  source_type: AppTextWidgetSourceType;
  source_widget_type: AppTextWidgetSourceWidgetType | null;
  source_widget_id: number | null;
  source_tab_id: number | null;
};

export type AppTextWidgetSummary = AppTextWidgetSourceFields & {
  id: number;
  app_id: number;
  tab_id: number | null;
  title: string | null;
  content: string;
  column_span: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type AppTextWidgetDataItem = AppTextWidgetSourceFields & {
  id: number;
  app_id: number;
  tab_id: number | null;
  title: string | null;
  content: string;
  column_span: number;
  sort_order: number;
};

function hasTextWidgetCoreFields(row: Record<string, unknown>): boolean {
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    (row.tab_id === null || typeof row.tab_id === "number") &&
    (row.title === null || typeof row.title === "string") &&
    typeof row.content === "string" &&
    isAppTextWidgetSourceType(row.source_type) &&
    isSourceWidgetType(row.source_widget_type ?? null) &&
    (row.source_widget_id === null ||
      row.source_widget_id === undefined ||
      typeof row.source_widget_id === "number") &&
    (row.source_tab_id === null ||
      row.source_tab_id === undefined ||
      typeof row.source_tab_id === "number") &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number"
  );
}

function isAppTextWidgetSummary(value: unknown): value is AppTextWidgetSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    hasTextWidgetCoreFields(row) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppTextWidgetDataItem(value: unknown): value is AppTextWidgetDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return hasTextWidgetCoreFields(value as Record<string, unknown>);
}

export async function fetchAppTextWidgets(appId: number): Promise<AppTextWidgetSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/text-widgets`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppTextWidgetSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export type AddTextWidgetToAppPayload = {
  tab_id?: number | null;
  title?: string | null;
  content: string;
  column_span?: number;
};

export async function addTextWidgetToApp(
  appId: number,
  payload: AddTextWidgetToAppPayload,
): Promise<AppTextWidgetSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/text-widgets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTextWidgetSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export type UpdateAppTextWidgetPayload = {
  tab_id?: number | null;
  /** An empty string clears the heading; omitting the key leaves it untouched. */
  title?: string;
  content?: string;
  column_span?: number;
};

export async function updateAppTextWidget(
  appId: number,
  textWidgetId: number,
  payload: UpdateAppTextWidgetPayload,
): Promise<AppTextWidgetSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/text-widgets/${textWidgetId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTextWidgetSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppTextWidget(appId: number, textWidgetId: number): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/text-widgets/${textWidgetId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
